import { useState, useEffect } from 'react';
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check, Send, Play, Pause } from 'lucide-react';
import { getAuthLink } from '@/lib/links';
import { HOME_AGENTS } from './homeData';

interface HeroMondayProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
}

interface TradeScenario {
  label: string;
  leadAgentId: string;
  leftAgentId: string;
  rightAgentId: string;
  prompt: string;
  badgeText: string;
  dashboardImage: string;
  dashboardAlt: string;
  dispatchReply: string;
}

const TRADE_SCENARIOS: TradeScenario[] = [
  {
    label: 'Residential Roofing',
    leadAgentId: 'ashley',
    leftAgentId: 'megan',
    rightAgentId: 'marcus',
    prompt: 'Book hail inspection for 4821 Ridgemont Drive, Plano TX',
    badgeText: 'Answered in 1.8 rings',
    dashboardImage: '/product-frames/leads.webp',
    dashboardAlt: 'Rooflyzer Leads workspace showing $208,600 pipeline value and active leads',
    dispatchReply: 'Ashley booked inspection & queued Tyler for roof estimate',
  },
  {
    label: 'Storm Restoration',
    leadAgentId: 'marcus',
    leftAgentId: 'ashley',
    rightAgentId: 'tyler',
    prompt: 'Open Marcus Whitfield file at 4821 Ridgemont Drive & send contract',
    badgeText: 'NOAA Doppler Live',
    dashboardImage: '/product-frames/lead-detail.webp',
    dashboardAlt: 'Rooflyzer Lead Detail workspace for Marcus Whitfield at 4821 Ridgemont Drive',
    dispatchReply: 'Marcus opened 4821 Ridgemont Drive lead file & staged contract',
  },
  {
    label: 'Commercial Roofing',
    leadAgentId: 'tyler',
    leftAgentId: 'marcus',
    rightAgentId: 'megan',
    prompt: 'Calculate measured roof estimate & sync $199k sales pipeline',
    badgeText: '84-Second Takeoff',
    dashboardImage: '/product-frames/pipeline.webp',
    dashboardAlt: 'Rooflyzer Sales Pipeline 4-stage board with $199k active pipeline value',
    dispatchReply: 'Tyler completed takeoff & updated Sales Pipeline board',
  },
  {
    label: 'Gutters & Siding',
    leadAgentId: 'megan',
    leftAgentId: 'tyler',
    rightAgentId: 'ashley',
    prompt: 'Issue invoice IN-20841-1 ($13,450) & track collection status',
    badgeText: '38-Second SMS Reply',
    dashboardImage: '/product-frames/invoices.webp',
    dashboardAlt: 'Rooflyzer Invoices workspace showing IN-20841-1 Paid $13,450',
    dispatchReply: 'Megan sent quote & Sofia recorded $13,450.00 paid invoice',
  },
];

export function HeroMonday({
  title = 'AI workspace for roofing contractors and their agents',
  subtitle = 'Autonomous AI agents answer homeowner calls 24/7, measure satellite roof pitch in 90 seconds, and collect milestone escrow draws. Built for roofing companies ready to scale.',
  ctaText = 'Get Started',
  ctaLink = '/register',
}: HeroMondayProps) {
  const shouldReduceMotion = useReducedMotion();
  const [selectedTrade, setSelectedTrade] = useState(0);
  const [autoCycle, setAutoCycle] = useState(true);
  const [demoPrompt, setDemoPrompt] = useState(TRADE_SCENARIOS[0].prompt);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [promptSubmitted, setPromptSubmitted] = useState(false);

  const scenario = TRADE_SCENARIOS[selectedTrade] || TRADE_SCENARIOS[0];
  const leadAgent = HOME_AGENTS.find((a) => a.id === scenario.leadAgentId) || HOME_AGENTS[0];
  const leftAgent = HOME_AGENTS.find((a) => a.id === scenario.leftAgentId) || HOME_AGENTS[1];
  const rightAgent = HOME_AGENTS.find((a) => a.id === scenario.rightAgentId) || HOME_AGENTS[3];

  useEffect(() => {
    if (!autoCycle || shouldReduceMotion || isEditingPrompt) return undefined;
    const cycleTimer = window.setTimeout(() => {
      const next = (selectedTrade + 1) % TRADE_SCENARIOS.length;
      setSelectedTrade(next);
      setDemoPrompt(TRADE_SCENARIOS[next].prompt);
    }, 6000);
    return () => window.clearTimeout(cycleTimer);
  }, [autoCycle, selectedTrade, shouldReduceMotion, isEditingPrompt]);

  const handleSelectTrade = (idx: number) => {
    setAutoCycle(false);
    setSelectedTrade(idx);
    if (!isEditingPrompt) {
      setDemoPrompt(TRADE_SCENARIOS[idx].prompt);
    }
  };

  const handleSelectAgentCard = (agentId: string, fallbackIdx: number) => {
    const matchedIdx = TRADE_SCENARIOS.findIndex((s) => s.leadAgentId === agentId);
    handleSelectTrade(matchedIdx !== -1 ? matchedIdx : fallbackIdx);
  };

  const handlePromptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!demoPrompt.trim()) return;
    setAutoCycle(false);
    setIsEditingPrompt(false);
    setPromptSubmitted(true);
    setTimeout(() => setPromptSubmitted(false), 3500);
  };

  return (
    <MotionConfig reducedMotion="user">
      <section className="relative overflow-hidden bg-white pt-8 pb-12 sm:pt-12 sm:pb-16 lg:pt-14 lg:pb-20">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* Left Column: Editorial Typography + Trade Segment Selector + Primary CTA */}
            <div className="lg:col-span-5 max-w-[540px]">
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="text-4xl sm:text-[50px] lg:text-[56px] font-normal tracking-[-0.04em] text-[#000000] leading-[1.14] mb-5"
              >
                {title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="text-base sm:text-[18px] text-[#323338] leading-[1.55] font-normal mb-7"
              >
                {subtitle}
              </motion.p>

              {/* Trade Segment Selector */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[13px] font-medium text-[#323338]">
                    Select a workspace view
                  </span>
                  <button
                    type="button"
                    onClick={() => setAutoCycle((prev) => !prev)}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#323338] hover:text-[#000000] transition-colors"
                  >
                    {autoCycle ? <Pause className="size-3" /> : <Play className="size-3" />}
                    <span>{autoCycle ? 'Auto-cycling' : 'Paused'}</span>
                  </button>
                </div>

                <div
                  className="flex flex-wrap items-center gap-2"
                  role="tablist"
                  aria-label="Trade segments"
                >
                  {TRADE_SCENARIOS.map((item, idx) => {
                    const isActive = selectedTrade === idx;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => {
                          setIsEditingPrompt(false);
                          setDemoPrompt(TRADE_SCENARIOS[idx].prompt);
                          handleSelectTrade(idx);
                        }}
                        className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs sm:text-[13px] border transition-all ${
                          isActive
                            ? 'bg-[#0B1120] text-white border-[#0B1120] font-medium'
                            : 'bg-[#F5F5F2] text-[#161616] border-gray-200 font-normal hover:bg-gray-200/70'
                        }`}
                      >
                        {isActive && <Check className="size-3.5 text-[#FBC72D] shrink-0" />}
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Primary CTA */}
              <div className="flex flex-col items-start">
                <a
                  href={getAuthLink(ctaLink)}
                  className="inline-flex min-h-12 items-center justify-center gap-3 px-8 py-3.5 rounded-lg font-semibold text-[16px] bg-[#FBC72D] text-[#161616] hover:bg-[#e5b320] shadow-xs transition-colors"
                >
                  <span>{ctaText}</span>
                  <ArrowRight className="size-4" />
                </a>

                <p className="mt-3.5 text-xs text-[#676879] flex items-center gap-2">
                  <span>No credit card needed</span>
                  <span aria-hidden="true">·</span>
                  <span>Full workspace access during beta</span>
                </p>
              </div>
            </div>

            {/* Right Column: 100% Unobstructed Real Rooflyzer Web-App Screen */}
            <div className="lg:col-span-7">
              <div className="w-full rounded-2xl bg-white border border-gray-200 shadow-[0_20px_60px_rgba(11,17,32,0.10)] overflow-hidden">
                {/* Full-Width Real Shipped Web-App Screenshot (No overlapping cards covering the UI) */}
                <div className="relative aspect-[16/10] w-full bg-[#F5F5F2] overflow-hidden border-b border-gray-200">
                  <img
                    src={scenario.dashboardImage}
                    alt={scenario.dashboardAlt}
                    className="w-full h-full object-cover object-left-top block"
                  />
                </div>

                {/* Clean Flat Footer Bar: Active Agent + Standby Switchers + Prompt Input */}
                <div className="p-3.5 sm:p-4 bg-white flex flex-col gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <img
                        src={leadAgent.portrait}
                        alt={leadAgent.name}
                        className="size-8 rounded-lg object-cover border border-gray-200 shrink-0"
                      />
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-[#161616] block truncate">
                          {leadAgent.name} · {leadAgent.role}
                        </span>
                        <span className="text-[11px] text-[#676879] block truncate">
                          {scenario.label} Active · {scenario.badgeText}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        aria-label={`Select ${leftAgent.name} (${leftAgent.role})`}
                        onClick={() =>
                          handleSelectAgentCard(
                            leftAgent.id,
                            (selectedTrade + TRADE_SCENARIOS.length - 1) % TRADE_SCENARIOS.length
                          )
                        }
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#F5F5F2] hover:bg-gray-200/70 border border-gray-200 text-left transition-colors"
                      >
                        <img
                          src={leftAgent.portrait}
                          alt={leftAgent.name}
                          className="size-5 rounded-full object-cover shrink-0"
                        />
                        <span className="text-[11px] font-medium text-[#161616]">
                          {leftAgent.name}
                        </span>
                      </button>

                      <button
                        type="button"
                        aria-label={`Select ${rightAgent.name} (${rightAgent.role})`}
                        onClick={() =>
                          handleSelectAgentCard(
                            rightAgent.id,
                            (selectedTrade + 1) % TRADE_SCENARIOS.length
                          )
                        }
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#F5F5F2] hover:bg-gray-200/70 border border-gray-200 text-left transition-colors"
                      >
                        <img
                          src={rightAgent.portrait}
                          alt={rightAgent.name}
                          className="size-5 rounded-full object-cover shrink-0"
                        />
                        <span className="text-[11px] font-medium text-[#161616]">
                          {rightAgent.name}
                        </span>
                      </button>
                    </div>
                  </div>

                  <form
                    onSubmit={handlePromptSubmit}
                    className="flex items-center gap-2 pt-2 border-t border-gray-100"
                  >
                    <input
                      type="text"
                      value={demoPrompt}
                      aria-label="Instruction for your AI fleet"
                      onFocus={() => {
                        setAutoCycle(false);
                        setIsEditingPrompt(true);
                      }}
                      onBlur={() => setIsEditingPrompt(false)}
                      onChange={(e) => {
                        setAutoCycle(false);
                        setIsEditingPrompt(true);
                        setDemoPrompt(e.target.value);
                      }}
                      placeholder="Ask your AI fleet to book, measure, or quote..."
                      className="flex-1 text-xs text-[#161616] placeholder:text-[#676879] bg-[#F5F5F2] border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#0B1120] focus:bg-white"
                    />
                    <button
                      type="submit"
                      aria-label="Send instructions"
                      className="size-8 rounded-lg bg-[#0B1120] text-white flex items-center justify-center hover:bg-[#1e293b] transition-colors shrink-0"
                    >
                      <Send className="size-3.5" />
                    </button>
                  </form>

                  <AnimatePresence>
                    {promptSubmitted && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-[11px] font-medium text-[#161616] flex items-center gap-1.5"
                      >
                        <Check className="size-3.5 text-[#15803d] shrink-0" />
                        <span className="truncate">{scenario.dispatchReply}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
